/**
 * Den Dateinamen einer Download-Antwort bestimmen.
 *
 * Seit die fünfzehn Berichte über `Tuv.Berichte.Layout` laufen, trägt jeder
 * Dateiname die Dokumentnummer vorn — `BER-8000-LUE-DRV-000003_….pdf`. Genau
 * dieser Name steht auch in der Registerzeile. Wer im Browser einen eigenen
 * Namen setzt, trennt die heruntergeladene Datei von ihrem Nachweis.
 *
 * Deshalb: Der Server bestimmt den Namen. Der mitgegebene Name ist nur der
 * Rückfall für Endpunkte ohne `Content-Disposition`.
 */

/**
 * Liest den Dateinamen aus `Content-Disposition`.
 *
 * `filename*` (RFC 5987, prozentkodiert mit Zeichensatz) geht vor `filename` —
 * ASP.NET Core schickt bei Umlauten BEIDE, und das ASCII-`filename` ist dann
 * die abgemagerte Fassung. Ein Regex, der einfach den ersten Treffer nimmt,
 * erwischt die falsche.
 *
 * Pfadanteile werden abgeschnitten: Ein Name aus einem fremden Kopf darf im
 * Browser kein Verzeichnis aufmachen.
 *
 * @param antwort Die Antwort des Servers.
 * @param rueckfall Der Name, der gilt, wenn der Kopf fehlt oder leer ist.
 */
export function dateinameAusAntwort(
  antwort: Response, rueckfall: string,
): string {
  const kopf = antwort.headers.get('content-disposition') ?? '';

  return dateinameAusKopf(kopf, rueckfall);
}

/**
 * Derselbe Schritt auf dem nackten Kopfwert — getrennt, damit er ohne
 * `Response` prüfbar ist.
 */
export function dateinameAusKopf(kopf: string, rueckfall: string): string {
  if (!kopf) return rueckfall;

  // Zuerst die kodierte Fassung: filename*=UTF-8''Anlagen%C3%BCbersicht.pdf
  const erweitert = /filename\*\s*=\s*([^;]+)/i.exec(kopf);

  if (erweitert) {
    const name = saeubere(entpacke(erweitert[1].trim()));
    if (name) return name;
  }

  // Sonst die einfache: filename="Bericht.pdf" oder filename=Bericht.pdf
  const einfach = /filename\s*=\s*("([^"]*)"|[^;]*)/i.exec(kopf);

  if (einfach) {
    const roh = (einfach[2] ?? einfach[1] ?? '').trim();
    const name = saeubere(roh);
    if (name) return name;
  }

  return rueckfall;
}

/**
 * `UTF-8''Anlagen%C3%BCbersicht.pdf` → `Anlagenübersicht.pdf`.
 *
 * Der Zeichensatzteil wird abgeworfen: Etwas anderes als UTF-8 schickt die API
 * nicht, und `decodeURIComponent` kennt ohnehin nur diesen. Schlägt das
 * Entpacken fehl (halbe Prozentfolge), bleibt der rohe Text — besser ein
 * schiefer Name als eine Ausnahme mitten im Download.
 */
function entpacke(wert: string): string {
  const ohneZeichensatz = wert.includes("''")
    ? wert.slice(wert.indexOf("''") + 2)
    : wert;

  const roh = ohneZeichensatz.replace(/^"|"$/g, '');

  try {
    return decodeURIComponent(roh);
  } catch {
    return roh;
  }
}

/** Pfadanteile und Steuerzeichen entfernen. */
function saeubere(name: string): string {
  const letzter = name.split(/[/\\]/).pop() ?? '';

  return letzter.replace(/[\u0000-\u001f\u007f]/g, '').trim();
}
